/**
 * Client-side shapes for the warehousing assistant.
 *
 * `AssistantEvent` mirrors the union the backend streams — see
 * `logixi-be/src/assistant/assistant-provider.ts`, which is the source of truth. It is
 * duplicated rather than shared because the two codebases are separate builds pinned to
 * different TypeScript versions; if the backend union changes, this changes with it.
 *
 * Everything the model writes lands in `answer` as a plain string and is rendered through
 * interpolation. Nothing here is ever passed to `innerHTML`: tool results carry supplier-
 * and operator-entered free text, so model output is untrusted input on the way back.
 */

/** One field changing on a proposed write, for an accurate before/after in the confirmation card. */
export interface AssistantProposalDiffEntry {
  field: string;
  oldValue: any;
  newValue: any;
}

/** A write the assistant wants to make. Nothing has happened yet - see AssistantProposal below. */
export interface AssistantProposalEventPayload {
  id: string;
  tool: string;
  summary: string;
  diff: AssistantProposalDiffEntry[];
  expiresAt: string;
}

/** One frame off the wire. Anything not in this union is ignored rather than guessed at. */
export type AssistantEvent =
  | { type: 'text'; text: string }
  | { type: 'tool'; name: string }
  | { type: 'proposal'; proposal: AssistantProposalEventPayload }
  | { type: 'done'; usage: AssistantUsage }
  | { type: 'error'; message: string };

/**
 * A proposed write shown inline in the transcript, with Confirm/Reject controls.
 *
 * Nothing has been applied when this is created — the backend only stores a pending record. It
 * stays exactly that until `confirming`/`status` say otherwise, set from the REST response to
 * confirm/reject, never from the model: the model plays no part in whether or how this resolves.
 */
export class AssistantProposal {
  id: string = '';
  tool: string = '';
  summary: string = '';
  diff: AssistantProposalDiffEntry[] = [];
  expiresAt: string = '';

  /** True while the confirm/reject request is in flight, so the buttons disable rather than double-fire. */
  confirming: boolean = false;

  status: 'pending' | 'executed' | 'rejected' | 'failed' | 'expired' = 'pending';
  errorMessage: string = '';

  constructor(payload: AssistantProposalEventPayload) {
    this.id = payload.id;
    this.tool = payload.tool;
    this.summary = payload.summary;
    this.diff = payload.diff || [];
    this.expiresAt = payload.expiresAt;
  }

  get isPending(): boolean {
    return this.status === 'pending';
  }
}

/**
 * Token counts for one answer.
 *
 * THE TWO INPUT FIGURES ARE DISJOINT, per the contract in `assistant-provider.ts`.
 * `inputTokens` is the portion billed at the full rate and EXCLUDES anything served from
 * cache; `cachedInputTokens` is the rest. Total input is the sum of the two.
 *
 * This matters to the screen and not only to the adapter. Rendering `inputTokens` as "in"
 * with `cachedInputTokens` beside it as a parenthetical reads as a total and a subset of
 * itself — and on a cached turn the "subset" is the larger number, which measured live was
 * 503 in against 14,958 cached. Use `totalInputTokens` for anything labelled as input.
 */
export class AssistantUsage {
  /** Input billed at the full rate. Excludes anything served from cache. */
  inputTokens: number = 0;

  /** Input served from the prompt cache, billed at a fraction of the full rate. */
  cachedInputTokens: number = 0;

  outputTokens: number = 0;
}

/** Total input for a turn: the full-rate portion plus the cached portion. */
export function totalInputTokens(usage: AssistantUsage): number {
  if (!usage) {
    return 0;
  }

  return usage.inputTokens + usage.cachedInputTokens;
}

/** Which model answers. Both were asked for; the operator picks per question. */
export type AssistantProviderKey = 'claude' | 'openai';

/**
 * A tool the assistant ran while answering, recorded in the order it was called.
 *
 * Kept on the turn after the answer finishes, not just while it runs. On a multi-tool
 * question most of the wait is tool round trips, and which data was consulted is the only
 * thing on the screen that says where a number came from.
 */
export class AssistantToolStep {
  name: string = '';

  constructor(name: string) {
    this.name = name;
  }
}

/**
 * One question and the answer to it.
 *
 * `answer` grows a fragment at a time as the stream arrives. `failed` is separate from
 * `finished` on purpose: an answer that stopped half way is not a short answer, and the
 * screen must not let a truncated one read as complete.
 */
export class AssistantTurn {
  question: string = '';
  provider: AssistantProviderKey = 'claude';

  /**
   * The answer, split into the runs the model wrote it in.
   *
   * One string per run, and a run ends where a tool call interrupts it. This is structure the
   * backend already sent — a turn arrives as text, then a `tool` event, then more text — so
   * keeping the runs apart is preserving what came over the wire rather than decorating it.
   *
   * Flattening them into one string is what produced "…it lists every warehouse.Two
   * warehouses:" on every multi-tool answer. The model sends no separator between the two,
   * and it should not have to: they are separate blocks, not one sentence. Note the fix is
   * NOT whitespace — `white-space: pre-wrap` was already on the answer element throughout, so
   * a newline would have rendered if one had ever arrived. None does.
   *
   * Every run is plain text. Rendered with `{{ }}`, never as markup.
   */
  answerRuns: string[] = [];

  /**
   * Set when a tool interrupts the answer, so the next text starts a new run.
   *
   * A flag rather than pushing an empty run on the tool event, because several tools can be
   * called back to back and that would leave blank paragraphs between them.
   */
  breakBeforeNextText: boolean = false;

  toolSteps: AssistantToolStep[] = [];

  /** Writes the assistant proposed while answering, in the order they were proposed. */
  proposals: AssistantProposal[] = [];

  /** The tool currently running, or '' when none is. Cleared on done and on error. */
  activeTool: string = '';

  streaming: boolean = false;
  finished: boolean = false;

  failed: boolean = false;
  errorMessage: string = '';

  usage: AssistantUsage = null;

  constructor(question: string, provider: AssistantProviderKey) {
    this.question = question;
    this.provider = provider;
  }

  hasAnswer(): boolean {
    return this.answerRuns.length > 0;
  }

  /**
   * The whole answer as one string, for replaying as history.
   *
   * Runs are joined with a blank line — the separation the screen shows. The model is told
   * what it said, in the shape the operator read it.
   */
  answerText(): string {
    return this.answerRuns.join('\n\n');
  }
}
